import {
  Address,
  Amount,
  CollateralMerklReward,
  DebtMerklReward,
  MerklReward,
  ProtectedMerklReward,
  Usd,
  USD_ZERO,
} from "@silo/types";
import { MerklCampaign } from "../merkl-client/types";
import { match } from "ts-pattern";
import { calculateRewardsApr } from "@silo/utils";

interface MarketAsset {
  tokenAddress: Address;
  symbol: string;
  collateralTotal: Amount;
  protectedTotal: Amount;
  debtTotal: Amount;
  priceUsd: Usd;
  decimals: number;
}

interface GetMerklRewardsByTypeReturnType {
  collateralMerklRewards: CollateralMerklReward[];
  protectedMerklRewards: ProtectedMerklReward[];
  debtMerklRewards: DebtMerklReward[];
}

export function getMerklRewardsByType(
  assetMerklCampaigns: MerklCampaign[],
  marketAsset: MarketAsset
): GetMerklRewardsByTypeReturnType {
  const merklRewards = assetMerklCampaigns.map((merklCampaign) =>
    getMerklReward(merklCampaign, marketAsset)
  );

  const collateralMerklRewards = merklRewards.flatMap((reward) =>
    reward.positionType === "collateral" ? [reward] : []
  );

  const protectedMerklRewards = merklRewards.flatMap((reward) =>
    reward.positionType === "protected" ? [reward] : []
  );

  const debtMerklRewards = merklRewards.flatMap((reward) =>
    reward.positionType === "debt" ? [reward] : []
  );

  return {
    collateralMerklRewards,
    protectedMerklRewards,
    debtMerklRewards,
  };
}

function getMerklReward(
  merklCampaign: MerklCampaign,
  marketAsset: MarketAsset
): MerklReward {
  const {
    tokenAddress,
    symbol,
    collateralTotal,
    protectedTotal,
    debtTotal,
    priceUsd,
    decimals,
  } = marketAsset;

  const {
    positionType,
    rewardTokenAddress,
    rewardTokenSymbol,
    rewardTokenDecimals,
    merklApr,
    isSuperFest,
    amountPerSecond,
    rewardTokenPriceUsd,
  } = merklCampaign;

  const totalTokens = match(positionType)
    .with("collateral", () => collateralTotal)
    .with("protected", () => protectedTotal)
    .with("debt", () => debtTotal)
    .exhaustive();

  const calculatedApr = calculateRewardsApr({
    emissionPerSecond: amountPerSecond,
    rewardTokenPriceUsd: rewardTokenPriceUsd ?? USD_ZERO,
    rewardTokenDecimals,
    totalTokens,
    tokenPriceUsd: priceUsd,
    tokenDecimals: decimals,
  });

  const apr = merklApr ?? calculatedApr;

  const merklReward: MerklReward = {
    positionType,
    targetTokenAddress: tokenAddress,
    targetTokenSymbol: symbol,
    rewardTokenAddress,
    rewardTokenSymbol,
    merklApr,
    calculatedApr,
    apr,
    isSuperFest,
  };

  return merklReward;
}
